<script setup lang="ts">
import type { IDashboardWidgetProps, TWidgetEmits } from '@tenorlab/vue-dashboard'
import { DashboardWidgetBase, useWidgetEmits } from '@tenorlab/vue-dashboard'

const props = defineProps<IDashboardWidgetProps>()
const emits = defineEmits<TWidgetEmits>()
const emitHandlers = useWidgetEmits(emits)

// Mock data
type TDataItem = {
  key: string
  label: string
  value: number
  valuePct: number
}

const getChangeColor = (change: number): string => {
  if (change > 0) return 'success'
  if (change < 0) return 'error'
  return 'disabled'
}

const getChangeIndicator = (change: number): string => {
  if (change > 0) return '▲'
  if (change < 0) return '▼'
  return '•'
}

const getCardCssClasses = (color: string): string => {
  return `flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 p-4 rounded-sm outline outline-${color} bg-cardchild content-cardchild`
}

const getPctCssClasses = (color: string): string => {
  return `text-xs font-medium text-${color}`
}

const getValueCssClasses = (): string => {
  return `w-full flex-none text-3xl/10 font-medium tracking-tight text-primary`
}

const getFormattedPct = (valuePct: number): string => {
  return `${Math.abs(valuePct * 100).toFixed(2)}%`
}

const getFormattedValue = (value: number): string => {
  return `$${value.toLocaleString()}`
}

const mockData: TDataItem[] = [
  {
    key: 'revenue',
    label: 'Revenue',
    value: 405091,
    valuePct: 0.0475,
  },
  {
    key: 'overdue',
    label: 'Overdue invoices',
    value: 12787,
    valuePct: 0.5402,
  },
  {
    key: 'outstanding',
    label: 'Outstanding invoices',
    value: 245988,
    valuePct: -0.0139,
  },
  {
    key: 'expenses',
    label: 'Expenses',
    value: 30156,
    valuePct: 0.1018,
  },
]
</script>

<template>
  <DashboardWidgetBase v-bind="props" direction="row" v-on="emitHandlers">
    <dl class="w-full grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
      <div v-for="item in mockData" key="item.key" :class="getCardCssClasses(getChangeColor(item.valuePct))">
        <dt class="font-medium">{item.label}</dt>
        <dd :class="getPctCssClasses(getChangeColor(item.valuePct))">
          <span>{{ getChangeIndicator(item.valuePct) }}</span>
          <span>{{ getFormattedPct(item.valuePct) }}</span>
        </dd>
        <dd :class="getValueCssClasses()">{{ getFormattedValue(item.value) }}</dd>
      </div>
    </dl>
  </DashboardWidgetBase>
</template>
